// app-state.service.ts
import { Injectable, signal } from '@angular/core';
import { DeviceService, DeviceType } from './device.service';
import { blankDataModel } from 'app/constants/conversation-data-models';
import { SubscriptionEntitlement, SubscriptionInfo } from './subscriptions.service';

@Injectable({ providedIn: 'root' })
export class AppStateService {
    constructor(private deviceService: DeviceService) {
        this.updateDeviceSignals(); // Initialize values

        // Watch for window resize
        window.addEventListener('resize', () => this.updateDeviceSignals());
    }

    readonly agent = {
        mode: signal<'agent' | 'results' | 'config-flight-option'>('agent'),
        ioMethod: signal<'keyboard' | 'voice'>(null),
        showInputAssist: signal(false),
        hideInput: signal(false),
        currentIntent: signal<string | null>(null),
        currentConversation: signal<any[]>([]),
        currentDataModel: signal<any>(JSON.parse(JSON.stringify(blankDataModel))),
        isHandlingQuery: signal(false),
        isSearchingFlights: signal(false),
        isRefiningFlights: signal(false),
        isLoadingOptions: signal(false),
        isAgentSpeaking: signal(false),
        refinedFlightOptions: signal<any>({}),
        isNoResult: signal(false),
        error: signal<string | null>(null),
        singleTTS: signal<any>({}),
        selectedOption: signal<any>(null),
    };

    readonly navigation = {
        backButtonState: signal<any>({}), // TODO TYPINGS
        currentURL: signal<string | null>(null),
    }


    readonly user = {
        profile: signal<any>({}),
        subscription: signal<SubscriptionInfo | null>(null),
        entitlement: signal<SubscriptionEntitlement | null>(null),
        flags: signal<Record<string, any>>({})
    }

    readonly device = {
        type: signal<DeviceType>('desktop'),
        isMobile: signal(false),
        isTablet: signal(false),
        isDesktop: signal(true),
        viewport: signal<{ width: number; height: number }>({ width: 0, height: 0 }),
        isKeyboardFocused: signal(false),
    };

    resetAgentState() {
        try {
            this.agent.mode.set('agent');
            this.agent.currentIntent.set(null);
            this.agent.currentConversation.set([]);
            this.agent.currentDataModel.set(JSON.parse(JSON.stringify(blankDataModel)));
            this.agent.isHandlingQuery.set(false);
            this.agent.isSearchingFlights.set(false);
            this.agent.isRefiningFlights.set(false);
            this.agent.isLoadingOptions.set(false);
            this.agent.isAgentSpeaking.set(false);
            this.agent.refinedFlightOptions.set([]);
            this.agent.isNoResult.set(false);
            this.agent.error.set(null);
            this.agent.singleTTS.set(null);
            this.agent.selectedOption.set(null);
            this.agent.hideInput.set(false);
        } catch (err) {
            console.error('there was an issue resetting agent state');
        }
    }

    resetUserState() {
        this.user.profile.set(null);
        this.user.subscription.set(null);
        this.user.entitlement.set(null);
        this.user.flags.set({});
    }

    resetNavigationState() {
        this.navigation.currentURL.set(null);
    }

    resetAppState() {
        try {
            this.resetAgentState();
            this.resetUserState();
            this.resetNavigationState();
        } catch (err) {
            console.error('there was an issue resetting global state');
        }
    }

    // UTILS
    private updateDeviceSignals() {
        const type = this.deviceService.getDeviceType();
        const size = this.deviceService.getDeviceViewport();

        this.device.type.set(type);
        this.device.isMobile.set(type === 'mobile');
        this.device.isTablet.set(type === 'tablet');
        this.device.isDesktop.set(type === 'desktop');
        this.device.viewport.set(size);
    }
}
